import {
	Box,
	ButtonBase,
	CircularProgress,
	Grid2,
	InputBase,
	Tooltip,
	Typography,
} from '@mui/material';
import { InfoIcon, RedFlag, SearchIcon } from '../../../assets/icons/Icons';
import FilterTag from '../../../components/FilterTag';
import { MAIN_THEME_COLOR, WHITE } from '../../../assets/theme/Colors';
import Avatar from '../../../assets/images/Avatar.png';
import { SuperAdminChatList, Tag } from '../../../types';
import { getLastChatDate } from '../../../Utils/Helpers';
import { TOOLTIPS } from '../../../Utils/Enums';

interface SelectedChat {
	phoneNumber: string;
	name: string;
	phoneNumberExtra: string;
	isConsultantNeeded: boolean;
	orgId: string;
}

interface SearchWithTagsSectionProps {
	title: string;
	placeholder: string;
	onSearch: (searchQuery: string) => void;
	searchedText: string;
	isMobile?: boolean;
}

interface RecentChatProps {
	chats: Array<SuperAdminChatList>;
	selectedPhoneNumber: string;
	onClickRecentChat: (chat: SelectedChat) => void;
	onSearchChatList: (name: string) => void;
	onChangeGridVisibility: () => void;
	loading: boolean;
	searchedText: string;
	isMobile?: boolean;
}

interface ChatNumbersListProps {
	chatListData: Array<SuperAdminChatList>;
	onViewRecentChat: (chat: SelectedChat) => void;
	selectedPhoneNumber: string;
	onSearchChatList: (search: string) => void;
	onChangeGridVisibility: () => void;
	loading: boolean;
	searchedText: string;
	isMobile?: boolean;
}

interface StatusBadgeProps {
	status: string;
	selected: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, selected }) => {
	const statusColor = selected
		? '#FFFFFF'
		: status === 'Escalated'
			? '#008236'
			: status === 'Assigned'
				? MAIN_THEME_COLOR
				: '#f97316';
	return (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				px: 1.5,
				py: 0.5,
				borderRadius: '999px',
				fontSize: '12px',
				fontWeight: 500,
				backgroundColor: `${statusColor}20`,
				color: statusColor,
			}}>
			<Box
				sx={{
					width: 8,
					height: 8,
					borderRadius: '50%',
					backgroundColor: statusColor,
					mr: 0.5,
				}}
			/>
			<Typography
				sx={{
					fontSize: '10px',
					fontWeight: 500,
					color: 'inherit',
				}}>
				{status}
			</Typography>
		</Box>
	);
};

const SearchWithTagsSection: React.FC<SearchWithTagsSectionProps> = ({
	title,
	placeholder,
	onSearch,
	searchedText,
	isMobile,
}) => {
	return (
		<Grid2 container flexDirection={'column'} width={'100%'}>
			<Grid2 container alignItems={'center'} paddingBottom={'5px'}>
				<Typography
					fontSize={'16px'}
					marginY={'5px'}
					fontWeight={'700'}>
					{title}
				</Typography>
				<Tooltip
					title={TOOLTIPS.numberList}
					leaveTouchDelay={5000}
					enterTouchDelay={0}
					placement={isMobile ? 'bottom' : 'right'}
					arrow>
					<Typography paddingLeft={'5px'} paddingTop={'8px'}>
						<InfoIcon />
					</Typography>
				</Tooltip>
			</Grid2>
			<Grid2
				container
				justifyContent={'space-between'}
				alignItems={'center'}
				color={'#777E90'}
				paddingX={'15px'}
				marginBottom={'10px'}
				width={'100%'}
				bgcolor={'#F5F4F6'}
				borderRadius={'5px'}>
				<SearchIcon />
				<InputBase
					value={searchedText}
					type={'text'}
					sx={{
						padding: '4px',
						width: '90%',
						color: '#777E90',
					}}
					placeholder={placeholder}
					onChange={e => onSearch(e.target.value)}
				/>
			</Grid2>
		</Grid2>
	);
};

function RecentChatWindow({
	chats,
	selectedPhoneNumber,
	onClickRecentChat,
	onSearchChatList,
	onChangeGridVisibility,
	loading,
	searchedText,
	isMobile,
}: RecentChatProps) {
	function isToPhoneNumberTooLong(number: string) {
		const size = isMobile ? 15 : 21;
		if (number.length > size) {
			return number.slice(0, size) + '...';
		}

		return number;
	}

	return (
		<Grid2
			flexDirection={'row'}
			padding={'10px'}
			width={'100%'}
			height={'75vh'}
			paddingBottom={'10px'}>
			<SearchWithTagsSection
				title="Conversations"
				placeholder="Search"
				isMobile={isMobile}
				onSearch={onSearchChatList}
				searchedText={searchedText}
			/>
			{chats.length > 0 ? (
				chats.map((value, index) => (
					<ButtonBase
						key={index}
						disableRipple
						sx={{
							width: '100%',
							paddingY: '5px',
						}}
						onClick={() => {
							onClickRecentChat({
								phoneNumber: value.phoneNumber,
								name: value.name,
								phoneNumberExtra: value.phoneNumber,
								isConsultantNeeded: value.isConsultantNeeded,
								orgId: value.orgId,
							});
							onChangeGridVisibility();
						}}>
						<Grid2
							container
							width={'100%'}
							padding={'15px'}
							flexDirection={'row'}
							bgcolor={
								value.phoneNumber === selectedPhoneNumber
									? MAIN_THEME_COLOR
									: '#FFFFFF'
							}
							border={'1px #E5E7EB solid'}
							alignItems={'center'}
							justifyContent={'space-between'}
							borderRadius={'10px'}>
							<Grid2
								component={'div'}
								flexDirection={'row'}
								container
								alignItems="center">
								<Box
									component={'img'}
									src={Avatar}
									width={40}
									height={40}
									loading="lazy"
									sx={{
										objectFit: 'cover',
										display: 'block',
									}}
								/>
								<Grid2
									flexDirection={'row'}
									paddingLeft={'10px'}>
									<Typography
										color={
											value.phoneNumber ===
											selectedPhoneNumber
												? '#FFFFFF'
												: '#000000'
										}
										textAlign={'start'}
										fontSize={'14px'}
										fontWeight={'600'}>
										{isToPhoneNumberTooLong(
											value.name === 'Unknown'
												? value.phoneNumber
												: value.name,
										)}
									</Typography>
									{value.name !== 'Unknown' && (
										<Typography
											color={
												value.phoneNumber ===
												selectedPhoneNumber
													? '#FFFFFF'
													: '#7c7c7c'
											}
											textAlign={'start'}
											fontSize={'12px'}
											fontWeight={'600'}>
											{isToPhoneNumberTooLong(
												value.phoneNumber,
											)}
										</Typography>
									)}
								</Grid2>
							</Grid2>
							<Grid2
								component={'div'}
								flexDirection={'column'}
								container
								alignItems="flex-end">
								<StatusBadge
									status={value.status}
									selected={
										value.phoneNumber ===
										selectedPhoneNumber
									}
								/>
							</Grid2>
						</Grid2>
					</ButtonBase>
				))
			) : loading ? (
				<Box
					sx={{
						display: 'flex',
						height: '100%',
						justifyContent: 'center',
						alignItems: 'center',
					}}>
					<CircularProgress sx={{ color: MAIN_THEME_COLOR }} />
				</Box>
			) : (
				<Grid2
					container
					paddingLeft={'10px'}
					height={'75vh'}
					flexDirection={'column'}
					justifyContent={'center'}
					alignItems={'center'}>
					<Typography
						fontSize={'12px'}
						padding={'20px'}
						color={'#98A2B3'}
						textAlign={'center'}>
						No Results Found
					</Typography>
				</Grid2>
			)}
		</Grid2>
	);
}

function ChatNumbersList({
	chatListData,
	onViewRecentChat,
	selectedPhoneNumber,
	onSearchChatList,
	loading,
	onChangeGridVisibility,
	searchedText,
	isMobile = false,
}: ChatNumbersListProps) {
	return (
		<Grid2 component="div" size={{ xs: 12, md: 4 }}>
			<Grid2
				sx={{
					backgroundColor: '#FFFFFF',
					height: '100%',
					overflowY: 'auto',
					'&::-webkit-scrollbar': {
						display: 'none',
					},
					scrollbarWidth: 'none',
					borderRadius: '5px',
				}}>
				<RecentChatWindow
					chats={chatListData}
					onClickRecentChat={onViewRecentChat}
					selectedPhoneNumber={selectedPhoneNumber}
					onSearchChatList={onSearchChatList}
					onChangeGridVisibility={onChangeGridVisibility}
					loading={loading}
					searchedText={searchedText}
					isMobile={isMobile}
				/>
			</Grid2>
		</Grid2>
	);
}

export default ChatNumbersList;
